import { Box, Typography, useTheme } from '@mui/material';
import Button from '@mui/material/Button';
import { AxiosResponse } from 'axios';
import DatePicker from 'components/date-picker';
import FlexBox from 'components/flex-box';

import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import TextField from 'components/text-field';
import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import React, { useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { CreateVirtualFishGroupInput } from 'views/planning-prognosis/features/view-fish-group/types/create-virtual-fish-group-input.type';
import { FishGroup } from 'views/planning-prognosis/features/view-fish-group/types/fish-group.type';
import { getSmoltType } from '../api/fish-group-stocking/get-smolt-types';
import { getFishGroupSpecies } from '../api/get-fish-group-species';
import { getFishGroupStrains } from '../api/get-fish-group-strains';
import { putVirtualFishGroup } from '../api/virtual-fish-group/update-virtual-fish-group';
import Paper from 'components/paper';

type Props = { fishGroup: FishGroup; isEditable: boolean };

const EditStocking: React.FC<Props> = ({ fishGroup, isEditable }) => {
  const intl = useIntl();
  const snackbarDispatch = useSnackbarDispatch();
  const { account } = useAccountState();
  const theme = useTheme();

  // #region FORMS
  const { handleSubmit, control, setValue, watch } = useForm<CreateVirtualFishGroupInput>({
    defaultValues: useMemo(() => {
      return {
        name: fishGroup.fishGroupName,
        stockingDate: fishGroup.stockingDate,
        numberOfStockedFish: fishGroup.stockingNumber,
        averageStockingWeight: fishGroup.stockingWeight,
        speciesId: fishGroup.speciesId,
        smoltTypeId: fishGroup.smoltType,
        seaCloudStrainId: fishGroup.seaCloudStrainId || 0,
      };
    }, [fishGroup]),
    shouldUnregister: false,
  });

  const valuesVFG = useWatch({
    control: control,
  });

  // #endregion FORMS

  // #region FETCH DATA

  const { data: strains } = useGet(
    ['getFishGroupStrains', account.company?.marketId, fishGroup?.fishGroupId],
    (context) => getFishGroupStrains(Number(context.queryKey[1])),
    {
      enabled: !!account.company?.marketId && !!fishGroup?.fishGroupId,
    }
  );

  const { data: smoltTypes } = useGet(
    ['getSmoltType', account.company?.marketId],
    ({ queryKey }) => getSmoltType(Number(queryKey[1])),
    {
      select: (data) =>
        data.sort((a, b) =>
          a.name.localeCompare(b.name, undefined, {
            numeric: true,
          })
        ),
      enabled: !!account.company?.marketId,
    }
  );

  const { data: species } = useGet(
    ['getFishGroupSpecies', account.company?.marketId],
    ({ queryKey }) => getFishGroupSpecies(Number(queryKey[1])),
    {
      select: (data) =>
        data.sort((a, b) =>
          a.name.localeCompare(b.name, undefined, {
            numeric: true,
          })
        ),
      enabled: !!account.company?.marketId,
    }
  );

  // #endregion FETCH DATA

  // #region POST DATA

  const updateFishGroup = usePost<AxiosResponse, CreateVirtualFishGroupInput>(
    (request) => putVirtualFishGroup(fishGroup.localSiteId, fishGroup.fishGroupId, request),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  // #endregion POST DATA

  return (
    <form
      onSubmit={handleSubmit((data) => {
        if (data.seaCloudStrainId === 0) {
          delete data.seaCloudStrainId;
        }
        updateFishGroup.mutate(data);
      })}>
      <Paper transparent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={5}>
          {/* TODO: add translations */}
          <Typography variant="h3" color={theme.palette.common.white}>
            Fish Group
          </Typography>
          <Button size="small" type="submit" sx={{ mb: { xs: 3, md: 0 }, minWidth: 80 }} disabled={!isEditable}>
            <FormattedMessage id="general.update" />
          </Button>
        </Box>
        <FlexBox>
          <DatePicker
            label={'stockingDate'}
            controller={{
              name: 'stockingDate',
              control: control,
              rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
            }}
          />
          <TextField
            fullWidth
            type="number"
            label={'Number of fish'}
            controller={{
              name: 'numberOfStockedFish',
              control: control,
              rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
            }}
          />
          <TextField
            fullWidth
            type="number"
            label={'Stocking weight, g'}
            controller={{
              name: 'averageStockingWeight',
              control: control,
              rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
            }}
          />
          <Select
            fullWidth
            label="Species"
            controller={{
              control: control,
              name: 'speciesId',
              rules: {
                required: intl.formatMessage({ id: 'form.rules.required' }),
                validate: {
                  invalidId: (v: number) => (v === 0 ? false : true),
                },
              },
            }}
            onChange={() => {
              setValue('smoltTypeId', 0);
              setValue('seaCloudStrainId', 0);
            }}>
            {species?.map((specie) => (
              <RadioSelectItem key={specie.id} value={specie.id} selected={specie.id === watch('speciesId')}>
                {specie.name}
              </RadioSelectItem>
            ))}
          </Select>
          <Select
            fullWidth
            label="Smolt type"
            controller={{
              control: control,
              name: 'smoltTypeId',
              rules: {
                required: intl.formatMessage({ id: 'form.rules.required' }),
                validate: {
                  invalidId: (v: number) => (v === 0 ? false : true),
                },
              },
            }}>
            {smoltTypes
              ?.filter((st) => st.speciesId === valuesVFG.speciesId)
              .map((smoltType) => (
                <RadioSelectItem key={smoltType.id} value={smoltType.id} selected={smoltType.id === watch('smoltTypeId')}>
                  {smoltType.name}
                </RadioSelectItem>
              ))}
          </Select>
          <Select
            fullWidth
            label="Strain"
            disabled={!isEditable && updateFishGroup.isPending}
            controller={{
              name: 'seaCloudStrainId',
              control: control,
            }}>
            {strains?.map((s) => (
              <RadioSelectItem key={s.id} value={s.id} selected={s.id === watch('seaCloudStrainId')}>
                {s.name}
              </RadioSelectItem>
            ))}
          </Select>
        </FlexBox>
      </Paper>
    </form>
  );
};

export default EditStocking;
